import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { 
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  ChartLegend,
  ChartLegendContent 
} from "@/components/ui/chart";
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  PieChart, 
  Pie, 
  Cell, 
  LineChart, 
  Line, 
  ResponsiveContainer,
  Area,
  AreaChart
} from "recharts";
import { 
  TrendingUp, 
  TrendingDown, 
  Activity, 
  Users, 
  Monitor, 
  Package, 
  AlertTriangle, 
  CheckCircle, 
  Clock, 
  Camera,
  Zap,
  Target,
  PieChart as PieChartIcon,
  BarChart3,
  Calendar,
  Eye
} from "lucide-react";
import { format, subDays, startOfMonth, endOfMonth, eachDayOfInterval, parseISO } from "date-fns";

interface CreditAnalyticsDashboardProps {
  organizationId: string | null;
}

const CHART_COLORS = {
  primary: "hsl(var(--primary))",
  secondary: "hsl(var(--secondary))",
  accent: "hsl(var(--accent))",
  muted: "hsl(var(--muted))",
  success: "hsl(142, 76%, 36%)",
  warning: "hsl(38, 92%, 50%)",
  error: "hsl(0, 84%, 60%)",
  info: "hsl(221, 83%, 53%)"
};

export const CreditAnalyticsDashboard = ({ organizationId }: CreditAnalyticsDashboardProps) => {
  // Fetch credit packages data
  const { data: creditPackages = [], isLoading: packagesLoading } = useQuery({
    queryKey: ['analytics-credit-packages', organizationId],
    queryFn: async () => {
      if (!organizationId) return [];
      
      const { data, error } = await supabase
        .from('device_credit_packages')
        .select(`
          *,
          orders (
            total_cameras,
            total_images,
            created_at,
            customer_user_id
          )
        `)
        .eq('organization_id', organizationId)
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      return data || [];
    },
    enabled: !!organizationId,
  });

  // Fetch audit data
  const { data: auditEntries = [], isLoading: auditLoading } = useQuery({
    queryKey: ['analytics-audit-trail', organizationId],
    queryFn: async () => {
      if (!organizationId) return [];
      
      const { data, error } = await supabase
        .from('device_assignment_audit')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(500);
      
      if (error) throw error;
      return data || [];
    },
    enabled: !!organizationId,
  });

  // Calculate comprehensive analytics
  const analytics = {
    // Credit Metrics
    totalCredits: creditPackages.reduce((sum, pkg) => sum + pkg.total_credits, 0),
    assignedCredits: creditPackages.reduce((sum, pkg) => sum + pkg.assigned_credits, 0),
    remainingCredits: creditPackages.reduce((sum, pkg) => sum + pkg.remaining_credits, 0),
    utilizationRate: 0,
    
    // Package Metrics
    totalPackages: creditPackages.length,
    userOwnedPackages: creditPackages.filter(pkg => pkg.assignment_type === 'user_owned').length,
    orderBasedPackages: creditPackages.filter(pkg => pkg.assignment_type === 'order_based').length,
    manualPackages: creditPackages.filter(pkg => pkg.assignment_type === 'manual').length,
    assignedPackages: creditPackages.filter(pkg => pkg.is_assigned).length,
    depletedPackages: creditPackages.filter(pkg => pkg.remaining_credits === 0).length,
    
    // Device Metrics
    devicesWithCredits: [...new Set(creditPackages.filter(pkg => pkg.device_id !== 'USER_OWNED').map(pkg => pkg.device_id))].length,
    averageCreditsPerDevice: 0,
    
    // Activity Metrics
    totalAuditEntries: auditEntries.length,
    conflictCount: auditEntries.filter(entry => entry.conflict_detected).length,
    recentActivity: auditEntries.filter(entry => 
      new Date(entry.created_at) > subDays(new Date(), 7)
    ).length,
    
    // Efficiency Metrics
    creditEfficiency: 0,
    allocationSuccess: 0,
  };

  // Calculate derived metrics
  analytics.utilizationRate = analytics.totalCredits > 0 
    ? Math.round((analytics.assignedCredits / analytics.totalCredits) * 100) 
    : 0;
  
  analytics.averageCreditsPerDevice = analytics.devicesWithCredits > 0 
    ? Math.round(analytics.assignedCredits / analytics.devicesWithCredits) 
    : 0;
  
  analytics.creditEfficiency = analytics.assignedCredits > 0 
    ? Math.round(((analytics.assignedCredits - analytics.remainingCredits) / analytics.assignedCredits) * 100) 
    : 0;
  
  analytics.allocationSuccess = auditEntries.length > 0 
    ? Math.round(((auditEntries.length - analytics.conflictCount) / auditEntries.length) * 100) 
    : 100;

  // Prepare chart data
  const assignmentTypeData = [
    { name: "User Owned", value: analytics.userOwnedPackages, color: CHART_COLORS.primary },
    { name: "Order Based", value: analytics.orderBasedPackages, color: CHART_COLORS.success },
    { name: "Manual", value: analytics.manualPackages, color: CHART_COLORS.info },
  ];

  const creditDistributionData = [
    { name: "Assigned", value: analytics.assignedCredits, color: CHART_COLORS.success },
    { name: "Remaining", value: analytics.remainingCredits, color: CHART_COLORS.warning },
  ];

  // Credit usage timeline (last 30 days)
  const timelineData = eachDayOfInterval({
    start: subDays(new Date(), 30),
    end: new Date()
  }).map(date => {
    const dayPackages = creditPackages.filter(pkg => 
      format(parseISO(pkg.created_at), 'yyyy-MM-dd') === format(date, 'yyyy-MM-dd')
    );
    
    const dayAuditEntries = auditEntries.filter(entry => 
      format(parseISO(entry.created_at), 'yyyy-MM-dd') === format(date, 'yyyy-MM-dd')
    );
    
    return {
      date: format(date, 'MMM dd'),
      credits: dayPackages.reduce((sum, pkg) => sum + pkg.total_credits, 0),
      allocations: dayAuditEntries.filter(entry => 
        entry.action.includes('allocation') || entry.action.includes('assignment')
      ).length,
      packages: dayPackages.length,
    };
  });

  // Top devices by credit allocation
  const deviceCreditData = creditPackages
    .filter(pkg => pkg.device_id !== 'USER_OWNED')
    .reduce((acc, pkg) => {
      const deviceId = pkg.device_id;
      if (!acc[deviceId]) {
        acc[deviceId] = { deviceId, totalCredits: 0, assignedCredits: 0, packages: 0 };
      }
      acc[deviceId].totalCredits += pkg.total_credits;
      acc[deviceId].assignedCredits += pkg.assigned_credits;
      acc[deviceId].packages += 1;
      return acc;
    }, {} as Record<string, any>);

  const topDevices = Object.values(deviceCreditData)
    .sort((a: any, b: any) => b.assignedCredits - a.assignedCredits)
    .slice(0, 10);

  // Activity patterns by hour
  const activityByHour = Array.from({ length: 24 }, (_, hour) => {
    const hourEntries = auditEntries.filter(entry => 
      new Date(entry.created_at).getHours() === hour
    );
    return {
      hour: `${hour}:00`,
      activity: hourEntries.length,
    };
  });

  const isLoading = packagesLoading || auditLoading;

  if (isLoading) {
    return (
      <div className="space-y-4">
        {Array.from({ length: 6 }).map((_, i) => (
          <Card key={i}>
            <CardContent className="p-6">
              <div className="animate-pulse">
                <div className="h-4 bg-muted rounded w-1/4 mb-2"></div>
                <div className="h-8 bg-muted rounded w-1/2"></div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  if (!organizationId) {
    return (
      <Card>
        <CardContent className="text-center py-8">
          <AlertTriangle className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
          <p className="text-muted-foreground">No organization selected for analytics.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Overview Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Credits</CardTitle>
            <Package className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{analytics.totalCredits.toLocaleString()}</div>
            <div className="flex items-center pt-1">
              <Progress value={analytics.utilizationRate} className="flex-1 mr-2" />
              <span className="text-xs text-muted-foreground">{analytics.utilizationRate}% used</span>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Active Packages</CardTitle>
            <CheckCircle className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{analytics.assignedPackages}</div>
            <p className="text-xs text-muted-foreground">
              {analytics.totalPackages - analytics.assignedPackages} awaiting assignment
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Device Coverage</CardTitle>
            <Monitor className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{analytics.devicesWithCredits}</div>
            <p className="text-xs text-muted-foreground">
              Avg {analytics.averageCreditsPerDevice} credits/device
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">System Health</CardTitle>
            <Activity className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">{analytics.allocationSuccess}%</div>
            <p className="text-xs text-muted-foreground">
              {analytics.conflictCount} conflicts detected
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Credit Distribution Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <PieChartIcon className="h-4 w-4" />
              Package Distribution
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ChartContainer
              config={{
                "User Owned": { label: "User Owned", color: CHART_COLORS.primary },
                "Order Based": { label: "Order Based", color: CHART_COLORS.success },
                "Manual": { label: "Manual", color: CHART_COLORS.info },
              }}
              className="h-[300px]"
            >
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={assignmentTypeData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={120}
                    paddingAngle={5}
                    dataKey="value"
                  >
                    {assignmentTypeData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <ChartTooltip content={<ChartTooltipContent />} />
                  <ChartLegend content={<ChartLegendContent />} />
                </PieChart>
              </ResponsiveContainer>
            </ChartContainer>
            <div className="mt-4 space-y-2">
              <div className="flex justify-between text-sm">
                <span>User Owned:</span>
                <Badge variant="outline">{analytics.userOwnedPackages}</Badge>
              </div>
              <div className="flex justify-between text-sm">
                <span>Order Based:</span>
                <Badge className="bg-green-100 text-green-800">{analytics.orderBasedPackages}</Badge>
              </div>
              <div className="flex justify-between text-sm">
                <span>Manual:</span>
                <Badge className="bg-blue-100 text-blue-800">{analytics.manualPackages}</Badge>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Target className="h-4 w-4" />
              Credit Utilization
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ChartContainer
              config={{
                Assigned: { label: "Assigned", color: CHART_COLORS.success },
                Remaining: { label: "Remaining", color: CHART_COLORS.warning },
              }}
              className="h-[300px]"
            >
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={creditDistributionData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={120}
                    paddingAngle={5}
                    dataKey="value"
                  >
                    {creditDistributionData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <ChartTooltip content={<ChartTooltipContent />} />
                  <ChartLegend content={<ChartLegendContent />} />
                </PieChart>
              </ResponsiveContainer>
            </ChartContainer>
            <div className="mt-4 space-y-3">
              <div className="space-y-1">
                <div className="flex justify-between text-sm">
                  <span>Utilization Rate</span>
                  <span className="font-medium">{analytics.utilizationRate}%</span>
                </div>
                <Progress value={analytics.utilizationRate} />
              </div>
              <div className="space-y-1">
                <div className="flex justify-between text-sm">
                  <span>Credit Efficiency</span>
                  <span className="font-medium">{analytics.creditEfficiency}%</span>
                </div>
                <Progress value={analytics.creditEfficiency} />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Activity Timeline */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="h-4 w-4" />
            30-Day Activity Timeline
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ChartContainer
            config={{
              credits: { label: "Credits", color: CHART_COLORS.primary },
              allocations: { label: "Allocations", color: CHART_COLORS.success },
              packages: { label: "Packages", color: CHART_COLORS.info },
            }}
            className="h-[300px]"
          >
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={timelineData}>
                <XAxis dataKey="date" />
                <YAxis />
                <ChartTooltip content={<ChartTooltipContent />} />
                <Area
                  type="monotone"
                  dataKey="credits"
                  stackId="1"
                  stroke={CHART_COLORS.primary}
                  fill={CHART_COLORS.primary}
                  fillOpacity={0.3}
                />
                <Area
                  type="monotone"
                  dataKey="allocations"
                  stackId="2"
                  stroke={CHART_COLORS.success}
                  fill={CHART_COLORS.success}
                  fillOpacity={0.3}
                />
              </AreaChart>
            </ResponsiveContainer>
          </ChartContainer>
        </CardContent>
      </Card>

      {/* Top Devices and Activity Patterns */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="h-4 w-4" />
              Top Devices by Credits
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {topDevices.slice(0, 8).map((device: any, index) => (
                <div key={device.deviceId} className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="text-xs">
                      #{index + 1}
                    </Badge>
                    <span className="font-mono text-sm">{device.deviceId}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="text-right">
                      <div className="text-sm font-medium">
                        {device.assignedCredits.toLocaleString()}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {device.packages} packages
                      </div>
                    </div>
                    <div className="w-20">
                      <Progress 
                        value={(device.assignedCredits / Math.max(...topDevices.map((d: any) => d.assignedCredits))) * 100} 
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Eye className="h-4 w-4" />
              Activity Heatmap (24h)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ChartContainer
              config={{
                activity: { label: "Activity", color: CHART_COLORS.primary },
              }}
              className="h-[300px]"
            >
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={activityByHour} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                  <XAxis dataKey="hour" />
                  <YAxis />
                  <ChartTooltip content={<ChartTooltipContent />} />
                  <Bar dataKey="activity" fill={CHART_COLORS.primary} />
                </BarChart>
              </ResponsiveContainer>
            </ChartContainer>
          </CardContent>
        </Card>
      </div>

      {/* System Insights */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader>
            <CardTitle className="text-sm flex items-center gap-2">
              <Zap className="h-4 w-4" />
              Quick Stats
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-sm">Depleted Packages</span>
              <Badge variant={analytics.depletedPackages > 0 ? "destructive" : "default"}>
                {analytics.depletedPackages}
              </Badge>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm">Recent Activity (7d)</span>
              <Badge className="bg-blue-100 text-blue-800">
                {analytics.recentActivity}
              </Badge>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm">Avg Credits/Device</span>
              <Badge variant="outline">
                {analytics.averageCreditsPerDevice}
              </Badge>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-sm flex items-center gap-2">
              <TrendingUp className="h-4 w-4" />
              Performance
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="space-y-1">
              <div className="flex justify-between text-sm">
                <span>Allocation Success</span>
                <span className="font-medium text-green-600">{analytics.allocationSuccess}%</span>
              </div>
              <Progress value={analytics.allocationSuccess} />
            </div>
            <div className="space-y-1">
              <div className="flex justify-between text-sm">
                <span>Credit Efficiency</span>
                <span className="font-medium">{analytics.creditEfficiency}%</span>
              </div>
              <Progress value={analytics.creditEfficiency} />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-sm flex items-center gap-2">
              <AlertTriangle className="h-4 w-4" />
              Alerts & Issues
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-sm">Assignment Conflicts</span>
              <Badge variant={analytics.conflictCount > 0 ? "destructive" : "default"}>
                {analytics.conflictCount}
              </Badge>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm">Unused Packages</span>
              <Badge variant="outline">
                {analytics.totalPackages - analytics.assignedPackages}
              </Badge>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm">System Status</span>
              <Badge className="bg-green-100 text-green-800">
                <CheckCircle className="h-3 w-3 mr-1" />
                Healthy
              </Badge>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};